import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { userHasRole, useUserSelfDetails } from '@steelbuy/data-access';
import { getSearchResultsHeadings, ListingBuyerModel, TranslateFunc, UserRole } from '@steelbuy/ts-shared';
import {
    ButtonColor,
    ButtonSize,
    ButtonTertiary,
    ButtonTertiaryOnLightM,
    Icon,
    IconIdentifier,
    LoadingSpinner,
    LoadingStatus,
    Tooltip,
    TooltipType,
} from '@steelbuy/ui-primitive';
import { listingTableConfig } from '@steelbuy/util';
import { createRouteUrl, RoutePath } from '../../router/Routes';

import './ListingsTable.scss';

export type ListingsTableProps = {
    listings?: ListingBuyerModel[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formData?: Record<string, any>;
    loadMore: () => void;
    hasNextPage?: boolean;
    isFetching: boolean;
    isFetchingNextPage: boolean;
    total?: number;
    detailsPath: RoutePath;
    isInitialLoading?: boolean;
    goBackPath?: string;
};

export const ListingsTable = ({
    listings = [],
    formData,
    loadMore,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    total,
    detailsPath,
    isInitialLoading,
    goBackPath,
}: ListingsTableProps) => {
    const { t } = useTranslation(['translation', 'domainModel', 'uiDomain']);
    const { data: userModel } = useUserSelfDetails();
    const hasBuyerRole = userHasRole(userModel || null, [UserRole.BUYER]);
    const navigate = useNavigate();

    if ((listings.length === 0 && !isFetching && !isInitialLoading) || !formData) {
        return null;
    }

    const isLoading = (isFetching && !isFetchingNextPage) || isInitialLoading;

    const tableHeadings = getSearchResultsHeadings(formData);

    return (
        <div className={classNames('listings-table', { 'listings-table--loading': isLoading })}>
            {total && total > 0 ? (
                <div className="table-wrapper">
                    <div className="table-container">
                        <table>
                            <thead>
                                <tr>
                                    {tableHeadings.map((heading) => (
                                        <th key={heading}>
                                            <div className="table-heading">
                                                <span>
                                                    {listingTableConfig[heading]?.getHeading(
                                                        formData,
                                                        t as TranslateFunc
                                                    )}
                                                </span>
                                                {listingTableConfig[heading]?.getTooltip ? (
                                                    <Tooltip
                                                        content={listingTableConfig[heading]?.getTooltip(
                                                            t as TranslateFunc
                                                        )}
                                                        type={TooltipType.WARN}
                                                        shadow
                                                    >
                                                        <span>
                                                            <Icon name={IconIdentifier.ERROR} />
                                                        </span>
                                                    </Tooltip>
                                                ) : null}
                                            </div>
                                        </th>
                                    ))}
                                    <th>{/* for actions */}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {listings.map((listing) => (
                                    <tr
                                        key={listing.id}
                                        onClick={() =>
                                            navigate(createRouteUrl(detailsPath, ['listingId', listing.id]), {
                                                state: goBackPath ? { goBackPath } : undefined,
                                            })
                                        }
                                    >
                                        {tableHeadings.map((heading) => (
                                            <td key={heading}>
                                                {listingTableConfig[heading]?.mapValue?.(listing, t as TranslateFunc)}
                                            </td>
                                        ))}
                                        <td>
                                            <div className="listings-table__actions-cell">
                                                {hasBuyerRole && (
                                                    <ButtonTertiary
                                                        label={t(`application.dashboardListings.viewDeliveredPrice`)}
                                                        size={ButtonSize.S}
                                                        color={ButtonColor.DARK}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : null}
            {hasNextPage && (
                <div className="listings-table__load-more">
                    <ButtonTertiaryOnLightM
                        onClick={loadMore}
                        label={t('uiDomain:commonList.loadMore')}
                        loadingStatus={isFetchingNextPage ? LoadingStatus.PENDING : LoadingStatus.IDLE}
                    />
                </div>
            )}
            {isLoading ? (
                <div className="loading-overlay">
                    <LoadingSpinner fillContainerHeight />
                </div>
            ) : null}
        </div>
    );
};
